// 이미지 및 동영상 파일 검증을 위한 유틸리티 함수들

// 허용된 이미지 MIME 타입들
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
];

// 허용된 이미지 파일 확장자들
export const ALLOWED_IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
];

// 허용된 동영상 MIME 타입들
export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/mpeg",
  "video/quicktime", // .mov
  "video/x-msvideo", // .avi
  "video/webm",
  "video/x-matroska", // .mkv
  "video/x-m4v", // .m4v
  "video/3gpp", // .3gp
];

// 허용된 동영상 파일 확장자들
export const ALLOWED_VIDEO_EXTENSIONS = [
  ".mp4",
  ".mov",
  ".avi",
  ".webm",
  ".mkv",
  ".m4v",
  ".3gp",
];

// 파일이 이미지인지 확인하는 함수
export const isImageFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  
  // 파일 확장자 확인
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext)
  );

  // MIME 타입 확인 (file.type이 있을 때만)
  const hasValidMimeType = file.type ? ALLOWED_IMAGE_TYPES.includes(file.type) : false;

  // 확장자 또는 MIME 타입 중 하나라도 일치하면 유효
  return hasValidExtension || hasValidMimeType;
};

// 파일 크기 제한 확인 (maxSizeMB가 undefined이면 제한 없음)
export const isFileSizeValid = (
  file: File,
  maxSizeMB?: number
): boolean => {
  // maxSizeMB가 없으면 용량 제한 없음
  if (maxSizeMB === undefined) {
    return true;
  }
  const maxSizeBytes = maxSizeMB * 1024 * 1024; // MB를 바이트로 변환
  return file.size <= maxSizeBytes;
};

// 파일이 동영상인지 확인하는 함수
export const isVideoFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  
  // 파일 확장자 확인
  const hasValidExtension = ALLOWED_VIDEO_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext)
  );

  // MIME 타입 확인 (file.type이 있을 때만)
  const hasValidMimeType = file.type ? ALLOWED_VIDEO_TYPES.includes(file.type) : false;

  // 확장자 또는 MIME 타입 중 하나라도 일치하면 유효
  return hasValidExtension || hasValidMimeType;
};

// 파일이 이미지 또는 동영상인지 확인하는 함수
export const isMediaFile = (file: File): boolean => {
  return isImageFile(file) || isVideoFile(file);
};

// 이미지 파일 검증 (타입 + 크기)
export const validateImageFile = (
  file: File,
  maxSizeMB?: number
): { isValid: boolean; error?: string } => {
  if (!isImageFile(file)) {
    return {
      isValid: false,
      error: "이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)",
    };
  }

  if (!isFileSizeValid(file, maxSizeMB)) {
    return {
      isValid: false,
      error: maxSizeMB
        ? `파일 크기는 ${maxSizeMB}MB 이하여야 합니다.`
        : "파일 크기가 너무 큽니다.",
    };
  }

  return { isValid: true };
};

// 동영상 파일 검증 (타입 + 크기, maxSizeMB는 옵션)
export const validateVideoFile = (
  file: File,
  maxSizeMB?: number
): { isValid: boolean; error?: string } => {
  if (!isVideoFile(file)) {
    return {
      isValid: false,
      error: "동영상 파일만 업로드 가능합니다. (MP4, MOV, AVI, WEBM, MKV, M4V)",
    };
  }

  if (!isFileSizeValid(file, maxSizeMB)) {
    return {
      isValid: false,
      error: maxSizeMB
        ? `파일 크기는 ${maxSizeMB}MB 이하여야 합니다.`
        : "파일 크기가 너무 큽니다.",
    };
  }

  return { isValid: true };
};

// 미디어 파일 검증 (이미지 또는 동영상)
export const validateMediaFile = (
  file: File,
  maxSizeMB?: number
): { isValid: boolean; error?: string; mediaType?: "image" | "video" } => {
  if (isImageFile(file)) {
    const result = validateImageFile(file, maxSizeMB);
    return { ...result, mediaType: "image" };
  }

  if (isVideoFile(file)) {
    const result = validateVideoFile(file, maxSizeMB);
    return { ...result, mediaType: "video" };
  }

  return {
    isValid: false,
    error: "이미지 또는 동영상 파일만 업로드 가능합니다.",
  };
};

// 여러 파일 검증 (이미지만)
export const validateImageFiles = (
  files: FileList | File[],
  maxSizeMB?: number
): { validFiles: File[]; errors: string[] } => {
  const fileArray = Array.from(files);
  const validFiles: File[] = [];
  const errors: string[] = [];

  fileArray.forEach((file) => {
    const validation = validateImageFile(file, maxSizeMB);
    if (validation.isValid) {
      validFiles.push(file);
    } else {
      errors.push(`${file.name}: ${validation.error}`);
    }
  });

  return { validFiles, errors };
};

// 여러 미디어 파일 검증 (이미지 + 동영상)
export const validateMediaFiles = (
  files: FileList | File[],
  maxSizeMB?: number
): {
  validFiles: File[];
  errors: string[];
  mediaTypes: Array<{ file: File; type: "image" | "video" }>;
} => {
  const fileArray = Array.from(files);
  const validFiles: File[] = [];
  const errors: string[] = [];
  const mediaTypes: Array<{ file: File; type: "image" | "video" }> = [];

  fileArray.forEach((file) => {
    const validation = validateMediaFile(file, maxSizeMB);
    if (validation.isValid && validation.mediaType) {
      validFiles.push(file);
      mediaTypes.push({ file, type: validation.mediaType });
    } else {
      errors.push(`${file.name}: ${validation.error}`);
    }
  });

  return { validFiles, errors, mediaTypes };
};

// 이미지 파일 미리보기 URL 생성
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error("이미지 파일이 아닙니다."));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      resolve(result);
    };
    reader.onerror = () => {
      reject(new Error("파일 읽기 실패"));
    };
    reader.readAsDataURL(file);
  });
};

// 미디어 파일 미리보기 URL 생성 (이미지 또는 동영상)
export const createMediaPreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!isMediaFile(file)) {
      reject(new Error("이미지 또는 동영상 파일이 아닙니다."));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      resolve(result);
    };
    reader.onerror = () => {
      reject(new Error("파일 읽기 실패"));
    };
    reader.readAsDataURL(file);
  });
};

